import {ElementUtil} from '../../utils/elementUtil.js';
import {TimeUtil} from '../../utils/timeUtil.js';
import {showMigrationErrorDialog} from './migrationErrorDialog.js';
import {showMigrationStatusDialog} from './migrationStatusDialog.js';
import {
	buildMigrationOptionFields,
	canResyncMigration,
	canRetryMigration,
	collectResyncOptionData,
	isStuckPending,
	isStuckWorking,
	loadMigrationOptionsOntoForm,
	makeDialogButtonBar
} from './migrationOptionFields.js';
import {
	buildMigrationRunStepFields,
	loadMigrationStepOptions
} from './migrationStepOptions.js';

export function showMigrationSyncHistoryDialog(primaryRecord, migrationService, onRefresh) {
	if (!primaryRecord || !primaryRecord.migrationId) {
		return;
	}

	var dialog = ElementUtil.create({
		tag: 'dl-dialog',
		title: 'Sync History',
		icon: 'vaadin:clock',
		width: .8,
		height: .8,
		listeners: {
			ready: function() {
				dialog.show();

				var header = ElementUtil.create({
					tag: 'div',
					style: 'margin-bottom: 8px;',
					children: [
						{tag: 'div', trustedInnerHTML: true, innerHTML: '<strong>V6:</strong> ' + (primaryRecord.v6CompanyName || '')
							+ ' (' + (primaryRecord.v6CompanyId || '') + ')'},
						{tag: 'div', trustedInnerHTML: true, innerHTML: '<strong>V8:</strong> ' + (primaryRecord.companyName || '')}
					]
				});

				var toolbar = ElementUtil.create({
					tag: 'dl-toolbar',
					theme: 'spacing',
					children: [
						{
							tag: 'dl-action-button',
							itemid: 'refresh',
							'class': 'spacer-toolbar-item',
							children: [ElementUtil.makeIcon('vaadin:refresh', ' Refresh')],
							permissions: ['ADMIN_DATA_MIGRATION:READ'],
							listeners: {click: loadHistory}
						},
						{
							tag: 'dl-action-button',
							itemid: 'newResync',
							'class': 'spacer-toolbar-item',
							children: [ElementUtil.makeIcon('vaadin:plus', ' New Re-Sync')],
							properties: {disabled: !canResyncMigration(primaryRecord)},
							permissions: ['ADMIN_DATA_MIGRATION:WRITE'],
							listeners: {click: openNewResyncForm}
						},
						{
							tag: 'dl-action-button',
							itemid: 'details',
							'class': 'spacer-toolbar-item',
							children: [ElementUtil.makeIcon('vaadin:info-circle', ' Details')],
							properties: {disabled: true},
							permissions: ['ADMIN_DATA_MIGRATION:READ'],
							listeners: {click: openDetails}
						},
						{
							tag: 'dl-action-button',
							itemid: 'retry',
							'class': 'spacer-toolbar-item',
							children: [ElementUtil.makeIcon('vaadin:rotate-right', ' Retry')],
							properties: {disabled: true},
							permissions: ['ADMIN_DATA_MIGRATION:WRITE'],
							listeners: {click: actionRetry}
						}
					]
				});

				var toolbarContainer = ElementUtil.create({
					tag: 'div',
					slot: 'toolbar',
					children: [
						header,
						toolbar
					]
				});

				var grid = ElementUtil.create({
					tag: 'vaadin-grid',
					theme: 'row-stripes column-borders',
					style: 'height: 100%;',
					slot: 'primary',
					defaults: {
						tag: 'vaadin-grid-column',
						autoWidth: true
					},
					children: [
						{path: 'migrationTypeDescription', header: 'Type'},
						{
							path: 'runStatusDescription',
							header: 'Status',
							renderer: function(root, column, rowData) {
								var status = rowData.item.runStatusDescription || '';
								if (rowData.item.runStatus === 'ERROR'
									|| isStuckWorking(rowData.item)
									|| isStuckPending(rowData.item)) {
									var suffix = isStuckWorking(rowData.item) || isStuckPending(rowData.item)
										? ' (retry available)'
										: '';
									root.innerHTML = '<span style="color: #d32f2f;">' + status + suffix + '</span>';
								} else {
									root.textContent = status;
								}
							}
						},
						{
							path: 'dataStartDate',
							header: 'Start',
							renderer: function(root, column, rowData) {
								if (rowData.item.dataStartDate) {
									var date = TimeUtil.parseDate(rowData.item.dataStartDate);
									root.innerHTML = TimeUtil.formatDateStandard(date);
								} else {
									root.innerHTML = 'N/A';
								}
							}
						},
						{
							path: 'dataEndDate',
							header: 'End',
							renderer: function(root, column, rowData) {
								if (rowData.item.dataEndDate) {
									var date = TimeUtil.parseDate(rowData.item.dataEndDate);
									root.innerHTML = TimeUtil.formatDateStandard(date);
								} else {
									root.innerHTML = 'N/A';
								}
							}
						},
						{
							path: 'createDts',
							header: 'Created',
							renderer: function(root, column, rowData) {
								if (rowData.item.createDts) {
									var date = TimeUtil.parseDate(rowData.item.createDts);
									root.innerHTML = TimeUtil.formatDateStandard(date);
								} else {
									root.innerHTML = 'N/A';
								}
							}
						},
						{path: 'note', header: 'Note', flexGrow: 1}
					],
					listeners: {
						'active-item-changed': function(event) {
							var selected = event.detail.value;
							grid.selectedItems = selected ? [selected] : [];
							ElementUtil.query(toolbar, 'details').disabled = !selected;
							ElementUtil.query(toolbar, 'retry').disabled = !selected || !canRetryMigration(selected);
						}
					}
				});

				var emptyMessage = ElementUtil.create({
					tag: 'div',
					itemid: 'emptyMessage',
					slot: 'footer',
					style: 'color: var(--lumo-secondary-text-color);',
					innerHTML: ''
				});

				var layout = ElementUtil.create({
					tag: 'dl-flex-layout',
					children: [
						toolbarContainer,
						grid,
						emptyMessage
					]
				});
				dialog.addToContentElement(layout);

				function loadHistory() {
					var loadMask = grid.showLoading ? grid.showLoading() : null;
					// Include the primary INITIAL run so Step 1 results are visible via Details
					// even when no re-sync children exist yet.
					Promise.all([
						migrationService.getMigration(primaryRecord.migrationId),
						migrationService.getSyncHistory(primaryRecord.migrationId)
					]).then(function(results) {
						if (loadMask) {
							loadMask.remove();
						}
						var primary = results[0].data || results[0];
						Object.assign(primaryRecord, primary);
						var syncRuns = results[1].data || [];
						grid.activeItem = null;
						grid.items = [primary].concat(syncRuns).sort(function(left, right) {
							var leftDate = left && left.createDts ? new Date(left.createDts).getTime() : 0;
							var rightDate = right && right.createDts ? new Date(right.createDts).getTime() : 0;
							return rightDate - leftDate;
						});
						emptyMessage.innerHTML = syncRuns.length === 0
							? 'Select the Initial Migration row and click Details to review Step 1 results. Re-sync runs will appear here after they are submitted.'
							: '';
						ElementUtil.query(toolbar, 'details').disabled = true;
						ElementUtil.query(toolbar, 'retry').disabled = true;
						ElementUtil.query(toolbar, 'newResync').disabled = !canResyncMigration(primaryRecord);
					});
				}

				function openDetails() {
					var selected = grid.activeItem;
					if (!selected) {
						return;
					}
					migrationService.getMigration(selected.migrationId).then(function(fullRecord) {
						var migration = fullRecord.data || fullRecord;
						if (migration.runStatus === 'ERROR'
							|| isStuckWorking(migration)
							|| isStuckPending(migration)) {
							showMigrationErrorDialog(migration, migrationService, loadHistory);
							return;
						}
						showMigrationStatusDialog(migration, migrationService, loadHistory);
					});
				}

				function actionRetry() {
					var selected = grid.activeItem;
					if (!selected || !canRetryMigration(selected)) {
						return;
					}
					migrationService.getMigration(selected.migrationId).then(function(fullRecord) {
						var migration = fullRecord.data || fullRecord;
						showMigrationErrorDialog(migration, migrationService, loadHistory);
					});
				}

				function openNewResyncForm() {
					if (!canResyncMigration(primaryRecord)) {
						return;
					}

					var resyncDialog = ElementUtil.create({
						tag: 'dl-dialog',
						title: 'New Re-Sync',
						icon: 'vaadin:refresh',
						width: .55,
						height: .7,
						listeners: {
							ready: function() {
								resyncDialog.show();
								// Re-sync is a separate run: only date range / pull history / note.
								// Primary options (cleanup, lookback, archive) stay on the primary
								// record and are inherited by the server when omitted.
								var optionFields = buildMigrationOptionFields({
									includePrimaryOptions: false,
									includeReplaceExisting: true
								});
								var runStepFields = buildMigrationRunStepFields({
									migrationPhase: 'INITIAL'
								});
								var formPanel = ElementUtil.create({
									tag: 'dl-form-panel',
									itemid: 'form',
									children: [{
										tag: 'vaadin-form-layout',
										children: optionFields.fields.concat(runStepFields.fields)
									}]
								});
								resyncDialog.addToContentElement(formPanel);
								loadMigrationOptionsOntoForm(formPanel, primaryRecord, optionFields, {
									clearNote: true
								});
								loadMigrationStepOptions(migrationService).then(function(stepOptions) {
									runStepFields.bindStepOptions(stepOptions);
								});

								resyncDialog.addToContentElement(makeDialogButtonBar([
									{
										theme: 'primary',
										children: [ElementUtil.makeIcon('vaadin:database', ' Submit Re-Sync')],
										listeners: {
											click: function() {
												if (!formPanel.isValid()) {
													window.DL.MessageUtil.showToastError('Invalid form; check values');
													return;
												}
												var optionData = collectResyncOptionData(formPanel);
												migrationService.resyncMigration(primaryRecord.migrationId, Object.assign({
													migrationType: 'RESYNC'
												}, optionData, runStepFields.collectRunStepRequestData())).then(function() {
													window.DL.MessageUtil.showToast('Success', 'Re-sync submitted', {
														theme: 'success'
													});
													resyncDialog.close();
													loadHistory();
													if (onRefresh) {
														onRefresh();
													}
												});
											}
										}
									},
									{
										theme: 'tertiary',
										style: 'margin-left: 10px;',
										innerHTML: 'Cancel',
										listeners: {
											click: function() {
												resyncDialog.close();
											}
										}
									}
								]));
							}
						}
					});
				}

				loadHistory();
			}
		}
	});
}
